import { useTour } from '@reactour/tour'
import { BlockedFlow } from 'analytics/Analytics'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import RecurrentActionDialog from 'components/dialogs/RecurrentActionDialog'
import { groupcalStatusToGoogle } from 'components/eventComponents/GoingBar'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import {
  parseErrorToMessage,
  RECUR_SAVE_TYPE
} from 'components/popups/eventInfo/EventPopup'
import OutOfTierDialog from 'components/tier/OutOfTierDialog'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import {
  updateEventOnServer,
  updateParticipantStatus,
  UpdateParticipantStatusBody
} from 'services/api/event'
import Database from 'services/db/Database'
import { Account } from 'services/db/Queries/AccountQueries'
import { addTempEventToDate, localEventById } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import eventBus, { AppEvents } from 'services/EventBus'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier } from 'utils/api/accounts'
import {
  isGroupOutOfTier,
  isUserActiveInGroup,
  isUserAdminInGroup,
  isUserCanOperateEvents
} from 'utils/api/groups'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { isEventBooked } from 'utils/event'
import { CurrentConvertedCalendars } from 'utils/google_events'
import {
  groupIdToProvider,
  isConvertedGroup,
  isConvertedGroupId,
  ProviderType
} from 'utils/groups'
import { removePrefix } from 'utils/object'
import { EventShareUtils } from 'utils/share'

import { useCalendar } from './CalendarProvider'
import { useCalendarSync } from './CalendarSyncProdivder'

interface EventActionsContextType {
  deleteEvent: (
    event: EventDoc,
    group: GroupDoc | undefined,
    instanceStart: number,
    instanceEnd: number,
    deleteCallback?: () => void,
    startDeleteCallback?: () => void,
    status?: EventStatus,
    onDismiss?: () => void
  ) => void
  duplicateEvent: (
    event: EventDoc,
    instanceStart: number,
    instanceEnd: number
  ) => void
  shareEvent: (event: EventDoc) => Promise<void>
  shouldShowGoingBar: (event: EventDoc) => boolean
  changeRSVPStatus: (event: EventDoc, status: string) => Promise<void>
  isContextMenuOpen: boolean
  setIsContextMenuOpen: (isOpen: boolean) => void
  pendingDeleteEvent: {
    event: EventDoc
    instanceStart: number
    instanceEnd: number
    deleteCallback?: () => void
  } | null
  showDeleteForDialog: boolean
  setShowDeleteForDialog: (value: boolean) => void
}

const EventActionsContext = createContext<EventActionsContextType | undefined>(
  undefined
)

export const useEventActions = () => {
  const context = useContext(EventActionsContext)
  if (!context) {
    throw new Error(
      'useEventActions must be used within an EventActionsProvider'
    )
  }
  return context
}

interface EventActionsProviderProps {
  children: ReactNode
}

export const EventActionsProvider: React.FC<EventActionsProviderProps> = ({
  children
}) => {
  const calendarSync = useCalendarSync()
  const calendar = useCalendar()
  const tfmTourProvider = useTfmTourProvider()
  const tour = useTour()

  const [isUiBlocked, setIsUiBlocked] = useState(false)
  const [showDeleteForDialog, setShowDeleteForDialog] = useState(false)
  const [selectedRecurType, setSelectedRecurType] = useState<
    RECUR_SAVE_TYPE | undefined
  >(undefined)

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)
  const [showRecurrenceDialog, setShowRecurrenceDialog] = useState(false)
  const [showOutOfTierDialog, setShowOutOfTierDialog] = useState(false)
  const [outOfTierGroup, setOutOfTierGroup] = useState<GroupDoc | undefined>(
    undefined
  )
  const [outOfTierAccount, setOutOfTierAccount] = useState<
    AccountDoc | undefined
  >(undefined)
  const [pendingDeleteEvent, setPendingDeleteEvent] = useState<{
    event: EventDoc
    groupToDeleteFrom: GroupDoc | undefined
    instanceStart: number
    instanceEnd: number
    deleteCallback?: () => void
    startDeleteCallback?: () => void
    pendingStatus?: EventStatus
    onDismiss?: () => void
  } | null>(null)

  const showGroupOutOfTierDialog = (
    group: GroupDoc | undefined,
    account: AccountDoc
  ) => {
    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
    setOutOfTierGroup(group)
    setOutOfTierAccount(account)
    setShowDeleteForDialog(false)
    setShowRecurrenceDialog(false)
    setShowOutOfTierDialog(true)
  }

  const deleteEvent = (
    event: EventDoc,
    groupToDeleteFrom: GroupDoc | undefined,
    instanceStart: number,
    instanceEnd: number,
    deleteCallback?: () => void,
    startDeleteCallback?: () => void,
    status?: EventStatus,
    onDismiss?: () => void
  ) => {
    const isRecurrent =
      !!event.Recurrence &&
      typeof event.Recurrence !== 'string' &&
      Object.keys(event.Recurrence).length !== 0 &&
      ((event._id?.length ?? 0) > 0 || (event.local_id?.length ?? 0) > 0) &&
      event.someday !== '1'

    setPendingDeleteEvent({
      event,
      groupToDeleteFrom,
      instanceStart,
      instanceEnd,
      deleteCallback,
      startDeleteCallback,
      pendingStatus: status,
      onDismiss: onDismiss
    })

    localGroupById(event.GroupID ?? '').then((group) => {
      Account().then((account) => {
        const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        const isOutOfTier = isGroupOutOfTier(
          highestActiveTier(account),
          group,
          account
        )
        const readOnly = LocalStorage.get(LocalStorageKey.USER_ID) === null
        const isActive = isUserActiveInGroup(
          group,
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        )
        let isUiBlocked = false
        const theirEventsOnlyCheck =
          event.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID) &&
          group?.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents ===
            '1' &&
          !isUserAdminInGroup(
            group,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          )

        if (isGroupcalApp()) {
          isUiBlocked =
            isOutOfTier ||
            readOnly ||
            !isActive ||
            !isUserCanOperateEvents(group, userId) ||
            theirEventsOnlyCheck ||
            isEventBooked(event)
        } else {
          isUiBlocked = readOnly
        }

        if (
          isConvertedGroup(group) &&
          event.Type !== DocType.TASK &&
          event.Type !== DocType.NOTE &&
          group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
        ) {
          Logger.blue('EventPopup:converted group', CurrentConvertedCalendars)
          const provider = groupIdToProvider(group?._id ?? '')
          Logger.blue('EventPopup:provider', provider)

          if (group?.SyncProblem === '1' || isOutOfTier) {
            isUiBlocked = true
          } else {
            if (
              provider == ProviderType.GOOGLE ||
              provider === ProviderType.OUTLOOK
            ) {
              isUiBlocked =
                event.PersonalOwnerId !==
                CurrentConvertedCalendars.find(
                  (calendar) =>
                    calendar.id === removePrefix(event.ThirdPartyID ?? '')
                )?.primaryCalendarEmail
            }
          }
        }

        setIsUiBlocked(isUiBlocked)

        if (isOutOfTier) {
          showGroupOutOfTierDialog(group, account)
          return
        }

        if (isUiBlocked) {
          Logger.blue(
            'EventActionsProvider: show dialog',
            isOutOfTier,
            readOnly,
            !isActive,
            !isUserCanOperateEvents(group, userId),
            theirEventsOnlyCheck,
            isEventBooked(event)
          )
        }

        if (isRecurrent) {
          setShowRecurrenceDialog(true)
        } else {
          performDelete(
            event,
            group,
            instanceStart,
            instanceEnd,
            undefined,
            deleteCallback,
            startDeleteCallback,
            status
          )
        }
      })
    })
  }

  const executeDelete = (
    event: EventDoc,
    instanceStart: number,
    instanceEnd: number,
    recurType: RECUR_SAVE_TYPE | undefined,
    deleteCallback?: () => void,
    startDeleteCallback?: () => void,
    status?: EventStatus
  ) => {
    startDeleteCallback?.()
    if (event?._id) {
      // If it's a recurrent deletion via dialog
      if (recurType) {
        calendarSync
          .syncEventWithThirdParty(
            instanceStart,
            instanceEnd,
            {
              ...Database.toServerObject(event),
              Status:
                status ??
                (is24meApp() ? EventStatus.REMOVED : EventStatus.DELETED)
            },
            event as Model<EventDoc>, // Casting might be needed if event is not Model
            false,
            true,
            (e) => console.error(e),
            recurType,
            false
          )
          .then(() => {
            deleteCallback?.()
          })
      } else {
        localGroupById(event.GroupID ?? '').then((group) => {
          return localEventById(event._id ?? '').then((eventModel) => {
            updateEventOnServer(eventModel, {
              ...eventModel,
              Status:
                status ??
                (is24meApp() ? EventStatus.REMOVED : EventStatus.DELETED)
            })
              .then(() => {
                deleteCallback?.()
              })
              .catch((error) => {
                Logger.pink('error', error)
                deleteCallback?.()
                eventBus.publish(AppEvents.EventItemError, {
                  name: AppEvents.EventItemError,
                  data: {
                    error: error,
                    message: isOwnerForGroup(group)
                      ? parseErrorToMessage(error, event._id)
                      : t('calendarBlockedForChanges'),
                    title: t('cantDoThat')
                  }
                })
                return error
              })
          })
        })
      }
    }
    LocalStorage.remove(LocalStorageKey.DRAGGING)
    setPendingDeleteEvent(null)
    setShowDeleteForDialog(false)
    setShowRecurrenceDialog(false)
  }

  useEffect(() => {
    const shouldKeep =
      LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1'

    if (shouldKeep && !showRecurrenceDialog && !showDeleteForDialog) {
      setTimeout(() => {
        LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
      }, 250)
    }
  }, [showRecurrenceDialog, showDeleteForDialog])

  const performDelete = (
    event: EventDoc,
    currentGroup: GroupDoc | undefined,
    instanceStart: number,
    instanceEnd: number,
    recurType: RECUR_SAVE_TYPE | undefined,
    deleteCallback?: () => void,
    startDeleteCallback?: () => void,
    status?: EventStatus
  ) => {
    setSelectedRecurType(recurType)

    localGroupById(event.GroupID ?? '').then((groupModel) => {
      Account().then((account) => {
        const group = currentGroup ?? groupModel
        const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        const isOutOfTier = isGroupOutOfTier(
          highestActiveTier(account),
          group,
          account
        )
        const readOnly = LocalStorage.get(LocalStorageKey.USER_ID) === null
        const isActive = isUserActiveInGroup(
          group,
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        )
        let isUiBlocked = false
        const theirEventsOnlyCheck =
          event.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID) &&
          group?.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents ===
            '1' &&
          !isUserAdminInGroup(
            group,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          )

        if (isGroupcalApp()) {
          isUiBlocked =
            isOutOfTier ||
            readOnly ||
            !isActive ||
            !isUserCanOperateEvents(group, userId) ||
            theirEventsOnlyCheck ||
            isEventBooked(event)
        } else {
          isUiBlocked = readOnly
        }

        if (
          isConvertedGroup(group) &&
          event.Type !== DocType.TASK &&
          event.Type !== DocType.NOTE &&
          group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
        ) {
          Logger.blue('EventPopup:converted group', CurrentConvertedCalendars)
          const provider = groupIdToProvider(group?._id ?? '')
          Logger.blue('EventPopup:provider', provider)

          if (group?.SyncProblem === '1' || isOutOfTier) {
            isUiBlocked = true
          } else {
            if (
              provider == ProviderType.GOOGLE ||
              provider === ProviderType.OUTLOOK
            ) {
              isUiBlocked =
                event.PersonalOwnerId !==
                CurrentConvertedCalendars.find(
                  (calendar) =>
                    calendar.id === removePrefix(event.ThirdPartyID ?? '')
                )?.primaryCalendarEmail
            }
          }
        }

        Logger.pink('EventActionsProvider: performDelete called', group)
        Logger.pink('EventActionsProvider: isUiBlocked', isUiBlocked)
        Logger.pink(
          'EventActionsProvider: isOutOfTier',
          isOutOfTier,
          readOnly,
          !isActive,
          !isUserCanOperateEvents(group, userId),
          theirEventsOnlyCheck,
          isEventBooked(event)
        )

        setIsUiBlocked(isUiBlocked)

        if (isOutOfTier) {
          showGroupOutOfTierDialog(group, account)
          return
        }

        if (
          isOwnerForGroup(group) ||
          isUserAdminInGroup(group, userId) ||
          is24meApp() ||
          (event.ThirdPartyID?.length ?? 0) > 0
        ) {
          LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
          executeDelete(
            event,
            instanceStart,
            instanceEnd,
            recurType,
            deleteCallback,
            startDeleteCallback,
            status
          )
        } else {
          setShowDeleteForDialog(true)
        }
      })
    })
  }

  const handleSaveRecurringDelete = (type: RECUR_SAVE_TYPE) => {
    if (pendingDeleteEvent) {
      performDelete(
        pendingDeleteEvent.event,
        pendingDeleteEvent.groupToDeleteFrom,
        pendingDeleteEvent.instanceStart,
        pendingDeleteEvent.instanceEnd,
        type,
        pendingDeleteEvent.deleteCallback,
        pendingDeleteEvent.startDeleteCallback,
        pendingDeleteEvent.pendingStatus
      )
    }

    LocalStorage.remove(LocalStorageKey.DRAGGING)
  }

  const handleCloseRecurrenceDialog = () => {
    setShowRecurrenceDialog(false)
    pendingDeleteEvent?.onDismiss?.()
  }

  const duplicateEvent = (
    event: EventDoc,
    instanceStart: number,
    instanceEnd: number
  ) => {
    const dayjsInstance = dayjs.unix(instanceStart / 1000)

    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
    calendar.onGoToDate(dayjsInstance.toDate())
    addTempEventToDate(
      Number(dayjsInstance.unix()),
      Number(instanceEnd / 1000),
      event.GroupID ?? '',
      event.AllDay === '1',
      event.Type,
      undefined,
      event.ThirdPartyID,
      'duplicated',
      event.TaskType
    ).then((newEvent) => {
      Database.write(async () => {
        newEvent.update((local) => {
          local.Reminder = event.Reminder ?? []
          local.Text = event.Text
          local.Note = event.Note
          local.Recurrence = event.Recurrence
          local.Location = event.Location
          local.Label = event.Label
          local.Notes = event.Notes
        })
      })
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
      LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
      LocalStorage.remove(LocalStorageKey.DRAGGING)

      setTimeout(() => {
        if (tour.isOpen) {
          return
        }
        LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
        if (tour.setSteps && tfmTourProvider.getStepsByMode) {
          tour.setSteps(
            tfmTourProvider.getStepsByMode(ONBOARDING_MODE.DUPLICATED)
          )
          tfmTourProvider.setMode(ONBOARDING_MODE.DUPLICATED)
        }
        tour.setCurrentStep(0)
        tour.setIsOpen(true)
      }, 1000)
    })
  }

  const shareEvent = async (event: EventDoc) => {
    const shareText = EventShareUtils.buildEventShareText({
      text: event.Text,
      note: event.Note !== '' ? event.Note : undefined,
      notes:
        (typeof event.Notes !== 'string' &&
          event.Notes?.map((note) => ({ note: note.Note }))) ||
        undefined,
      startDate: event.StartDate,
      endDate: event.EndDate,
      allDay: event.AllDay === '1',
      location:
        typeof event.Location !== 'string' ? event.Location?.Address : undefined
    })

    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText
        })
      } catch (e) {
        console.error('Error sharing:', e)
      }
    }
  }

  const shouldShowGoingBar = (event: EventDoc) => {
    return (
      (event._id?.length ?? 0) > 0 &&
      (event.RequestConfirmation === '1' ||
        ((event.attendees ?? [])?.length > 0 &&
          !!event.attendees?.find((attendee) => attendee.self)))
    )
  }

  const changeRSVPStatus = async (event: EventDoc, status: string) => {
    const isThirdParty = isConvertedGroupId(event.GroupID)
    if (isThirdParty) {
      const updatedAttendees =
        event.attendees?.map((a) => {
          if (a.self) {
            a.responseStatus =
              groupIdToProvider(event.GroupID ?? '') === ProviderType.APPLE
                ? groupcalStatusToGoogle(status).toUpperCase()
                : groupcalStatusToGoogle(status)
          }
          return a
        }) ?? []
      await updateEventOnServer(event, {
        ...event,
        attendees: updatedAttendees,
        attendeesChanged: true
      })
    } else {
      const updatedStatus =
        event.ParticipantsStatus?.[
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        ]
      if (updatedStatus) {
        updatedStatus.ParticipantConfirmationStatus = status
        await updateParticipantStatus([
          {
            _id: event._id,
            DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID) ?? '',
            ParticipantsStatus: { ...updatedStatus }
          }
        ])
      }
    }
  }

  const handleDeleteForAll = async () => {
    if (!pendingDeleteEvent) return
    executeDelete(
      pendingDeleteEvent.event,
      pendingDeleteEvent.instanceStart,
      pendingDeleteEvent.instanceEnd,
      selectedRecurType,
      pendingDeleteEvent.deleteCallback,
      pendingDeleteEvent.startDeleteCallback
    )
    setShowRecurrenceDialog(false)
    setPendingDeleteEvent(null)
    setShowDeleteForDialog(false)
  }

  const handleDeleteForMe = async () => {
    const event = pendingDeleteEvent?.event
    const onClose = pendingDeleteEvent?.deleteCallback ?? (() => {})
    const updatedStatus =
      event?.ParticipantsStatus?.[
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ]
    if (updatedStatus) {
      const participantStatus = {
        ...updatedStatus,
        ParticipantConfirmationStatus: '3'
      }
      const updateBody: UpdateParticipantStatusBody = {
        _id: event._id,
        DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID) ?? '',
        ParticipantsStatus: participantStatus
      }
      // eventHook.setLoading(true)
      await updateParticipantStatus([updateBody])
      // eventHook.setLoading(false)
    }
    onClose()
    setPendingDeleteEvent(null)
    setShowDeleteForDialog(false)
    setShowRecurrenceDialog(false)
  }

  Logger.pink('EventActionsProvider: render', {
    isUiBlocked,
    showRecurrenceDialog,
    pendingDeleteEvent,
    showDeleteForDialog,
    selectedRecurType
  })

  return (
    <EventActionsContext.Provider
      value={{
        deleteEvent,
        duplicateEvent,
        shareEvent,
        shouldShowGoingBar,
        changeRSVPStatus,
        isContextMenuOpen,
        setIsContextMenuOpen,
        pendingDeleteEvent,
        showDeleteForDialog,
        setShowDeleteForDialog
      }}
    >
      {children}
      {showRecurrenceDialog && (
        <RecurrentActionDialog
          open={showRecurrenceDialog}
          default={isUiBlocked ? RECUR_SAVE_TYPE.ALL : undefined}
          disableOthers={isUiBlocked}
          handleClose={handleCloseRecurrenceDialog}
          handleSaveRecurringEvent={handleSaveRecurringDelete}
          item={pendingDeleteEvent?.event}
        />
      )}
      {showDeleteForDialog && (
        <ConfirmationDialog
          title={t('wouldYouLikeToRemove')}
          content={''}
          rightButtonDisabled={
            selectedRecurType && selectedRecurType !== RECUR_SAVE_TYPE.ALL
          }
          leftButtonDisabled={isUiBlocked}
          leftButton={`${t('removeForAll')}`}
          rightButton={`${t('removeForMe')}`}
          handleRightButton={handleDeleteForMe}
          handleLeftButton={handleDeleteForAll}
          open={showDeleteForDialog}
          handleClose={() => {
            setShowDeleteForDialog(false)
            pendingDeleteEvent?.onDismiss?.()
          }}
          followForm={false}
        />
      )}
      {showOutOfTierDialog && outOfTierAccount && (
        <OutOfTierDialog
          account={outOfTierAccount}
          flow={BlockedFlow.editItem}
          group={outOfTierGroup}
          setOpen={setShowOutOfTierDialog}
          opened={showOutOfTierDialog}
          onClose={() => {
            setTimeout(() => {
              LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
            }, 251)
          }}
        />
      )}
    </EventActionsContext.Provider>
  )
}
